import React from 'react';
import ContentBlocks from './ContentBlocks';
import { AddressAutocompleteInput } from './AddressAutocompleteInput';
import {
  Field,
  ChoiceGroup,
  TextInput,
  TextArea,
  Select,
} from '../onyx/Field';
import './QuestionRenderer.css';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS',
  'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
  'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

/**
 * Renders a single question via Onyx Field / ChoiceGroup.
 *
 * presentation="prompt" — survey-style heading (pre-screen / walkthrough).
 * presentation="field" — compact Field labels (onboarding forms).
 */
export default function QuestionRenderer({
  question,
  value,
  onChange,
  disabled,
  mapsLoaded = false,
  presentation = 'prompt',
}) {
  if (!question) {
    return null;
  }

  const useFieldPresentation = presentation === 'field';
  const density = useFieldPresentation ? 'compact' : 'comfortable';
  const booleanLayout = useFieldPresentation ? 'pair' : 'stack';

  const renderAddressFields = () => {
    const addressValue = value && typeof value === 'object' ? value : {
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      zip_code: '',
    };
    const zipInvalid = addressValue.zip_code?.length > 0 && addressValue.zip_code.length !== 5;
    const idPrefix = `q-${question.id || 'address'}`;
    const onFieldChange = (fieldOrPatch, fieldValue) => {
      // Object patch applies city/state/zip together (avoids stale overwrite).
      if (fieldOrPatch && typeof fieldOrPatch === 'object' && !Array.isArray(fieldOrPatch)) {
        onChange({ ...addressValue, ...fieldOrPatch });
        return;
      }
      onChange({ ...addressValue, [fieldOrPatch]: fieldValue });
    };
    const required = question.required !== false;

    return (
      <div className="preferences-fields" data-testid="question-address-fields">
        <AddressAutocompleteInput
          value={addressValue}
          onFieldChange={onFieldChange}
          mapsLoaded={mapsLoaded}
          idPrefix={idPrefix}
          required={required}
        />
        <Field density="compact" label="Address Line 2 (Optional)" htmlFor={`${idPrefix}-address-2`}>
          <TextInput
            density="compact"
            id={`${idPrefix}-address-2`}
            value={addressValue.address_2 || ''}
            disabled={disabled}
            onChange={(e) => onFieldChange('address_2', e.target.value)}
            placeholder="Apt, suite, unit, etc."
            autoComplete="address-line2"
          />
        </Field>
        <div className="preferences-address-grid onyx-field-grid--address">
          <Field density="compact" label="City" htmlFor={`${idPrefix}-city`} required={required}>
            <TextInput
              density="compact"
              id={`${idPrefix}-city`}
              value={addressValue.city || ''}
              disabled={disabled}
              onChange={(e) => onFieldChange('city', e.target.value)}
              required={required}
              autoComplete="address-level2"
            />
          </Field>
          <Field density="compact" label="State" htmlFor={`${idPrefix}-state`} required={required}>
            <Select
              density="compact"
              id={`${idPrefix}-state`}
              value={addressValue.state || ''}
              disabled={disabled}
              onChange={(e) => onFieldChange('state', e.target.value)}
              required={required}
              autoComplete="address-level1"
            >
              <option value="" disabled>
                Select…
              </option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            density="compact"
            label="ZIP Code"
            htmlFor={`${idPrefix}-zip`}
            required={required}
            error={zipInvalid ? 'ZIP code must be 5 digits' : undefined}
          >
            <TextInput
              density="compact"
              id={`${idPrefix}-zip`}
              value={addressValue.zip_code || ''}
              disabled={disabled}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
                onFieldChange('zip_code', digits);
              }}
              required={required}
              inputMode="numeric"
              maxLength={5}
              pattern="[0-9]{5}"
              autoComplete="postal-code"
              aria-invalid={zipInvalid || undefined}
            />
          </Field>
        </div>
      </div>
    );
  };

  const renderControl = () => {
    switch (question.question_type) {
      case 'rich_content_only':
        return null;

      case 'boolean':
        return (
          <ChoiceGroup
            name={`q-${question.id || 'boolean'}`}
            type="radio"
            layout={booleanLayout}
            density={density}
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={value === true ? 'true' : value === false ? 'false' : undefined}
            onChange={(next) => onChange(next === 'true')}
          />
        );

      case 'multiple_choice':
        return (
          <ChoiceGroup
            name={`q-${question.id || 'multi'}`}
            type="checkbox"
            density={density}
            options={question.options || []}
            values={Array.isArray(value) ? value : []}
            onChange={onChange}
          />
        );

      case 'single_choice':
        return (
          <ChoiceGroup
            name={`q-${question.id || 'single'}`}
            type="radio"
            density={density}
            options={question.options || []}
            value={value}
            onChange={onChange}
          />
        );

      case 'text':
        return (
          <TextInput
            density={density}
            value={value || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            required={question.required}
            placeholder="Enter your answer"
          />
        );

      case 'textarea':
        return (
          <TextArea
            value={value || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            required={question.required}
            rows={4}
            placeholder="Enter your answer"
          />
        );

      case 'email':
        return (
          <TextInput
            density={density}
            type="email"
            value={value || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            required={question.required}
            placeholder="example@email.com"
          />
        );

      case 'phone_number':
        return (
          <TextInput
            density={density}
            type="tel"
            value={value || ''}
            disabled={disabled}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
              let formatted = '';
              if (digits.length > 0) formatted += `(${digits.slice(0, 3)}`;
              if (digits.length > 3) formatted += `) ${digits.slice(3, 6)}`;
              if (digits.length > 6) formatted += `-${digits.slice(6)}`;
              onChange(formatted);
            }}
            placeholder="(555) 555-5555"
            maxLength={14}
            required={question.required}
          />
        );

      case 'number':
        return (
          <TextInput
            density={density}
            type="number"
            value={value === null || value === undefined ? '' : value}
            disabled={disabled}
            onChange={(e) => {
              const v = e.target.value;
              onChange(v === '' ? null : Number(v));
            }}
            required={question.required}
            placeholder="Enter a number"
          />
        );

      case 'date':
        return (
          <TextInput
            density={density}
            type="date"
            value={value || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            required={question.required}
          />
        );

      case 'address':
        return renderAddressFields();

      default:
        return (
          <p className="help-text">
            Unsupported question type: <code>{question.question_type}</code>
          </p>
        );
    }
  };

  const control = renderControl();

  if (useFieldPresentation) {
    // Compound address: label + nested compact Fields (never Field-in-Field).
    if (question.question_type === 'address') {
      // Same compact Field label chrome as phone / choice questions (no Field-in-Field).
      return (
        <div
          className="question-renderer question-renderer--field question-renderer--compound"
          data-presentation="field"
        >
          <ContentBlocks blocks={question.content_blocks} />
          <div className="onyx-field onyx-field--compact question-renderer__compound-heading">
            {question.text ? (
              <p className="onyx-field__label question-renderer__prompt-label">
                {question.text}
                {question.required ? (
                  <span className="onyx-field__required" aria-hidden="true">*</span>
                ) : null}
              </p>
            ) : null}
            {question.description ? (
              <p className="onyx-field__help">{question.description}</p>
            ) : null}
          </div>
          {control}
        </div>
      );
    }

    if (question.question_type === 'rich_content_only') {
      return (
        <div className="question-renderer question-renderer--field" data-presentation="field">
          <ContentBlocks blocks={question.content_blocks} />
          {question.text ? (
            <h2 className="question-renderer__text">{question.text}</h2>
          ) : null}
          {question.description ? (
            <p className="onyx-field__help">{question.description}</p>
          ) : null}
        </div>
      );
    }

    return (
      <div className="question-renderer question-renderer--field" data-presentation="field">
        <ContentBlocks blocks={question.content_blocks} />
        <Field
          density="compact"
          label={question.text}
          required={Boolean(question.required)}
          help={question.description || undefined}
        >
          {control}
        </Field>
      </div>
    );
  }

  return (
    <div className="question-renderer" data-presentation="prompt">
      <div className="question-renderer__prompt">
        <h2 className="question-renderer__text">{question.text}</h2>
        {question.description ? (
          <p className="onyx-field__help question-renderer__help">
            {question.description}
          </p>
        ) : null}
      </div>
      <ContentBlocks blocks={question.content_blocks} />
      {control ? (
        <Field className="question-renderer__field">
          <div className="question-renderer__input">{control}</div>
        </Field>
      ) : null}
    </div>
  );
}
